// Sales → Reports, from sales_reports(): recurring revenue, organisations,
// licences, receivables, cash in, licence-months per month and the funnel.
// Plus a CSV of every account for the accountant.

import { fill, type Locale } from "../../i18n/dictionary";
import type { SalesDictionary } from "../../i18n/salesDictionary";
import { PROSPECT_STAGES, toCsv, type SalesAccount } from "../../data/sales";
import { Button, Card, ErrorPanel, LoadingLine, SR_ONLY, StatTile, formatMoney, formatMonth, formatNumber, useLoad } from "../admin/ui";
import { SectionTitle, StatusLine, downloadCsv, salesErrorCopy, useSales } from "./shared";
import { useState } from "react";

export function accountsCsv(list: SalesAccount[]): string {
  return toCsv([
    [
      "Organisation", "Kind", "Status", "Legal name", "Registration no.", "Country", "Tax id", "Billing email",
      "Contact name", "Contact email", "Contact phone", "Sales owner", "Source", "Family licences", "Live", "Claimed",
      "Family pays", "Zooylo share", "SST %", "Currency", "Payment terms (days)", "Contract end", "Outstanding",
    ],
    ...list.map((a) => [
      a.name, a.kind, a.status, a.legalName, a.registrationNo, a.country, a.taxId, a.billingEmail,
      a.contactName, a.contactEmail, a.contactPhone, a.salesOwner, a.source, a.familyAllowance, a.licencesLive, a.licencesClaimed,
      a.familyRetailPriceMonthly.toFixed(2), a.familyPriceMonthly.toFixed(2), a.sstRate, a.currency, a.paymentTermsDays, a.contractEnd, a.outstanding.toFixed(2),
    ]),
  ]);
}

export function Reports() {
  const { ds, t, locale } = useSales();
  const load = useLoad(() => ds.reports(), [ds]);
  const [msg, setMsg] = useState<{ tone: "attention"; text: string } | null>(null);

  async function exportAccounts() {
    setMsg(null);
    try {
      const list = await ds.accounts();
      downloadCsv(`zooylo-accounts-${new Date().toISOString().slice(0, 10)}.csv`, accountsCsv(list));
    } catch (err) {
      setMsg({ tone: "attention", text: salesErrorCopy(t, err) });
    }
  }

  return (
    <section aria-labelledby="reports-title">
      <SectionTitle
        id="reports-title"
        action={
          <Button variant="secondary" onClick={() => void exportAccounts()}>
            {t.reports.exportAccounts}
          </Button>
        }
      >
        {t.reports.title}
      </SectionTitle>
      <StatusLine msg={msg} />
      {load.loading && !load.data ? (
        <LoadingLine label={t.common.loading} />
      ) : load.error || !load.data ? (
        <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.common.retry} />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 10, margin: "4px 0 18px" }}>
            <StatTile label={t.reports.mrr} value={formatMoney(locale, load.data.mrr, load.data.currency)} hint={t.reports.mrrHint} />
            <StatTile
              label={t.reports.orgsActive}
              value={formatNumber(locale, load.data.organisationsActive, 0)}
              hint={fill(t.reports.orgsSuspended, { n: load.data.organisationsSuspended })}
            />
            <StatTile
              label={t.reports.licencesLive}
              value={formatNumber(locale, load.data.licencesLive, 0)}
              hint={fill(t.reports.licencesClaimed, { n: load.data.licencesClaimed })}
            />
            <StatTile label={t.reports.receivables} value={formatMoney(locale, load.data.receivables, load.data.currency)} />
            <StatTile label={t.reports.paid90} value={formatMoney(locale, load.data.paidLast90Days, load.data.currency)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))", gap: 14 }}>
            <Card title={t.reports.lmTitle}>
              <MonthBars data={load.data.licenceMonthsByMonth} locale={locale} label={t.reports.lmChart} />
            </Card>
            <Card title={t.reports.funnelTitle}>
              <Funnel funnel={load.data.funnel} t={t} locale={locale} />
            </Card>
          </div>
        </>
      )}
    </section>
  );
}

/** Plain SVG bars, each labelled with its value and month (no colour-only meaning). */
export function MonthBars({ data, locale, label }: { data: { month: string; licenceMonths: number }[]; locale: Locale; label: string }) {
  const W = 600;
  const H = 240;
  const top = 28;
  const bottom = 34;
  const max = Math.max(1, ...data.map((d) => d.licenceMonths));
  const slot = W / Math.max(data.length, 1);
  const barW = Math.min(64, slot * 0.6);
  return (
    <figure style={{ margin: 0, position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={label} style={{ display: "block", maxHeight: 260 }}>
        <line x1={0} x2={W} y1={H - bottom} y2={H - bottom} stroke="var(--line-strong)" strokeWidth={1} />
        {data.map((d, i) => {
          const h = ((H - top - bottom) * d.licenceMonths) / max;
          const x = i * slot + (slot - barW) / 2;
          const y = H - bottom - h;
          return (
            <g key={d.month}>
              <rect x={x} y={y} width={barW} height={Math.max(h, 1)} rx={6} fill="var(--brand)" />
              <text x={x + barW / 2} y={y - 8} textAnchor="middle" fontSize={15} fontWeight={700} fill="var(--ink-deep)">
                {formatNumber(locale, d.licenceMonths, 1)}
              </text>
              <text x={x + barW / 2} y={H - 10} textAnchor="middle" fontSize={14} fill="var(--ink-soft)">
                {formatMonth(locale, d.month, false)}
              </text>
            </g>
          );
        })}
      </svg>
      {/* A table for screen readers; a 1px box, since a table ignores its own width. */}
      <div style={SR_ONLY}>
      <table>
        <caption>{label}</caption>
        <tbody>
          {data.map((d) => (
            <tr key={d.month}>
              <th scope="row">{formatMonth(locale, d.month)}</th>
              <td>{formatNumber(locale, d.licenceMonths, 1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </figure>
  );
}

function Funnel({ funnel, t, locale }: { funnel: Record<string, number>; t: SalesDictionary; locale: Locale }) {
  const max = Math.max(1, ...PROSPECT_STAGES.map((s) => funnel[s] ?? 0));
  return (
    <dl style={{ margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
      {PROSPECT_STAGES.map((s) => {
        const n = funnel[s] ?? 0;
        return (
          <div key={s} style={{ display: "grid", gridTemplateColumns: "minmax(90px, 32%) 1fr auto", alignItems: "center", gap: 10 }}>
            <dt style={{ fontSize: 14, color: "var(--ink-soft)", fontWeight: 600 }}>{t.pipeline[`stage_${s}`]}</dt>
            <dd style={{ margin: 0, height: 14, background: "var(--surface-warm)", borderRadius: 7, overflow: "hidden" }} aria-hidden="true">
              <span style={{ display: "block", height: "100%", width: `${(n / max) * 100}%`, background: s === "lost" ? "var(--line-strong)" : "var(--brand)", borderRadius: 7 }} />
            </dd>
            <dd style={{ margin: 0, fontWeight: 700, color: "var(--ink-deep)", minWidth: 24, textAlign: "end" }}>{formatNumber(locale, n, 0)}</dd>
          </div>
        );
      })}
    </dl>
  );
}
